import { ValidatorFn, AbstractControl, ValidationErrors, FormGroup, AsyncValidatorFn, Validators } from '@angular/forms'
import * as moment from 'moment/moment'
import { Observable, of } from 'rxjs'

export const UAN_REGEX: RegExp = /^(?!0+$)(?=.{12}$)[0-9]*$/
export const BANK_ACCOUNT_NUMBER_PATTERN: RegExp = /^[0-9]{10,16}$/
export const IFSC_CODE_PATTERN: RegExp = /^[A-Za-z]{4}0\d{6}$/
export const SCHEME_CERTIFICATE_NUMBER_PATTERN: RegExp = /^(?!0+$)[a-zA-Z0-9/]{12}$/

export const ALPHA_NUMERIC_WITHOUT_SPACES_PATTERN: RegExp = /^(?!0+$)[a-zA-Z0-9]*$/
export const VALIDATORS = {
  PAN_NUMBER: Validators.pattern('^[A-Z]{5}[0-9]{4}[A-Z]$'),
  AADHAAR: Validators.pattern('^[0-9]{4}[ -]?[0-9]{4}[ -]?[0-9]{4}$'),
  EMAIL: Validators.email,
  MOBILE: Validators.pattern(/^(?!0+$)(?:\(?\+\d{1,3}\)?|0)?\d{10}$/gm),
  ONLY_NUMBER: Validators.pattern(/^-?(0|[1-9]\d*)?$/),
  ALPHA_NUMERIC: Validators.pattern(/^[a-zA-Z0-9]*$/),
  ONLY_ALPHABETS: Validators.pattern(/^[a-zA-Z]*$/),
  ONLY_APLHABETS_WITH_SPACES: Validators.pattern(/^[a-zA-Z\s]*$/),
  ALPHA_NUMERIC_WITH_SPACES: Validators.pattern(/^[a-zA-Z0-9\s]*$/),
  ALPHA_NUMERIC_WITHOUT_SPACES: Validators.pattern(ALPHA_NUMERIC_WITHOUT_SPACES_PATTERN)
}

export function passwordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s])[\S]{8,32}$/
    const valid = passwordRegex.test(control.value)
    return valid ? null : { invalidPassword: true }
  }
}

export function passwordMatchValidator(passwordKey: string, confirmPasswordKey: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup: AbstractControl = control.root
    const passwordControl: AbstractControl | null = formGroup.get(passwordKey)
    const confirmPasswordControl: AbstractControl | null = formGroup.get(confirmPasswordKey)

    passwordControl?.valueChanges.subscribe((value: string) => {
      if (
        passwordControl?.touched &&
        passwordControl?.dirty &&
        confirmPasswordControl?.touched &&
        confirmPasswordControl?.dirty
      ) {
        if (value !== confirmPasswordControl.value) {
          confirmPasswordControl.setErrors({ passwordMismatch: true })
        } else {
          confirmPasswordControl.setErrors(null)
        }
      }
    })

    if (passwordControl?.value == confirmPasswordControl?.value) {
      return null
    } else {
      return { passwordMismatch: true }
    }
  }
}

export function dateOfBirthValidator(minAge: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control && control.value)) {
      return null
    }
    const age = moment().diff(control.value, 'years')
    return age < minAge ? { invalidDob: true } : null
  }
}

export function futureDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control && control.value)) {
      return null
    }
    const selectedDate = new Date(control.value)
    const today = new Date()
    return selectedDate > today ? { futureDate: true } : null
  }
}

export function emailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null
    }
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/
    const valid = emailRegex.test(control.value?.toLowerCase())
    return valid ? null : { email: true }
  }
}

export const patternValidator = (pattern: RegExp, errorMessage: string): AsyncValidatorFn => {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (!control.value) {
      return of(null)
    }

    const valid = pattern.test(control.value)
    return valid ? of(null) : of({ invalid: errorMessage })
  }
}

export const panNumberAsyncValidator: AsyncValidatorFn = patternValidator(
  /^(?=.{10}$)[A-Z]{3}[P]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/,
  'Invalid Pan Number'
)

export const aadharNumberAsyncValidator: AsyncValidatorFn = patternValidator(
  /^(?=.{12}$)[0-9]*$/,
  'Invalid Aadhar Number'
)

export const uanNumberAsyncValidator: AsyncValidatorFn = patternValidator(UAN_REGEX, 'Invalid UAN Number')

export const pfNumberAsyncValidator: AsyncValidatorFn = patternValidator(/^[a-zA-Z0-9]{22}$/, 'Invalid PF Number')

export const mobileNumberAsyncValidator: AsyncValidatorFn = patternValidator(/^[0-9]{10}$/, 'Invalid Mobile Number')

export const pincodeAsyncValidator: AsyncValidatorFn = patternValidator(/^(?!0+$)[0-9]{6}$/, 'Invalid Pincode')
export const bankAccountNumberAsyncValidator: AsyncValidatorFn = patternValidator(
  BANK_ACCOUNT_NUMBER_PATTERN,
  'Invalid Bank Account Number'
)
export const ifscCodeAsyncValidator: AsyncValidatorFn = patternValidator(IFSC_CODE_PATTERN, 'Invalid IFSC Code')
export const schemeCertificateNumberAsyncValidator: AsyncValidatorFn = patternValidator(
  SCHEME_CERTIFICATE_NUMBER_PATTERN,
  'Invalid Scheme Certificate Number'
)

export function countryCodeValidator() {
  return (control: AbstractControl): ValidationErrors | null => {
    const countryCode = control.value
    if (!countryCode) {
      return { countryCodeInvalid: true }
    }
    const countryCodePattern = /^\+\d+$/
    if (!countryCodePattern.test(countryCode)) {
      return { countryCodeInvalid: true }
    }
    return null
  }
}

export function emailValidatorForNonMandatory(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/
    const value = control.value

    if (!value) {
      return null
    }

    const valid = emailRegex.test(value?.toLowerCase())
    return valid ? null : { email: true }
  }
}

export const ppoNumberAsyncValidator: AsyncValidatorFn = patternValidator(
  /^(?!0+$)[0-9]{12}$/,
  'Please enter a valid Pension Payment Order Number'
)

export const passportAsyncValidator: AsyncValidatorFn = patternValidator(
  /^[A-Za-z0-9]{5,20}$/,
  'Please enter a valid Passport Number'
)

export function pastDateAsyncValidator(control: AbstractControl): Observable<ValidationErrors | null> {
  if (!control?.value) {
    return of(null)
  }
  const selectedDate = new Date(control.value)
  const today = new Date()
  return of(selectedDate <= today ? { invalid: true } : null) // restrict current and past dates
}

export function futureDateAsyncValidator(control: AbstractControl): Observable<ValidationErrors | null> {
  if (!control?.value) {
    return of(null)
  }
  const selectedDate = new Date(control.value)
  const today = new Date()
  return of(selectedDate > today ? { invalid: true } : null) // allows past and current date
}

export const ALPHA_NUMERIC_WITHOUT_SPACES_ASYNC_VALIDATOR: AsyncValidatorFn = patternValidator(
  ALPHA_NUMERIC_WITHOUT_SPACES_PATTERN,
  'Invalid Input'
)

export function dateValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) {
      return null
    }

    const isValidDate = !isNaN(Date.parse(control.value))

    return isValidDate ? null : { invalidDate: true }
  }
}
